<script setup lang="ts">
import { ref, computed } from 'vue'
import { useSidebar } from '@/composables/useSidebar'
import { useAuthStore } from '@/stores/auth'
import ThemeToggler from '../common/ThemeToggler.vue'
import UserMenu from './header/UserMenu.vue'
import HeaderLogo from './header/HeaderLogo.vue'
import CompanyBadge from '@/components/badges/CompanyBadge.vue'
import type { CompanyType } from '@/types/user'

const { toggleSidebar, toggleMobileSidebar, isMobileOpen } = useSidebar()
const authStore = useAuthStore()

// Get user's company - safely access without any
const userCompany = computed<CompanyType | undefined>(() => {
  const user = authStore.user
  if (!user) return undefined

  // Type-safe way to access company property
  // The user object might have different formats, so we need to check
  if ('company' in user) {
    return (user as { company?: CompanyType }).company
  }

  return undefined
})

// Format company name for display
const companyDisplayName = computed(() => {
  const company = userCompany.value

  // Just check if company exists - no need for empty string check anymore
  if (!company) return null

  // Type-safe mapping
  if (company === 'maxstationary') return 'Max Stationary'
  if (company === 'ostnasik') return 'OST Nasik'
  if (company === 'shradhha-ent') return 'Shradhha Enterprises'

  return company
})

// Check if company exists
const hasCompany = computed(() => {
  return !!userCompany.value
})

const handleToggle = () => {
  if (window.innerWidth >= 1024) {
    toggleSidebar()
  } else {
    toggleMobileSidebar()
  }
}

const isApplicationMenuOpen = ref(false)

const toggleApplicationMenu = () => {
  isApplicationMenuOpen.value = !isApplicationMenuOpen.value
}
</script>

<template>
  <header
    class="sticky top-0 flex w-full bg-white lg:bg-gray-50 border-gray-200 z-99990 dark:border-gray-800 dark:bg-gray-900 border-b-0"
  >
    <div class="flex flex-col items-center justify-between grow lg:flex-row lg:py-2 lg:px-6">
      <div
        class="flex items-center justify-between w-full gap-2 px-3 py-3 border-b border-gray-200 dark:border-gray-800 sm:gap-4 lg:justify-normal lg:border-b-0 lg:p-0"
      >
        <button
          @click="handleToggle"
          class="flex items-center justify-center w-10 h-10 text-gray-500 border-gray-200 rounded-lg z-99999 dark:border-gray-800 dark:text-gray-400 lg:h-11 lg:w-11 lg:border"
          :class="[
            isMobileOpen
              ? 'lg:bg-transparent dark:lg:bg-transparent bg-gray-100 dark:bg-gray-800'
              : '',
          ]"
        >
          <svg
            v-if="isMobileOpen"
            class="fill-current"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M6.21967 7.28131C5.92678 6.98841 5.92678 6.51354 6.21967 6.22065C6.51256 5.92775 6.98744 5.92775 7.28033 6.22065L11.999 10.9393L16.7176 6.22078C17.0105 5.92789 17.4854 5.92788 17.7782 6.22078C18.0711 6.51367 18.0711 6.98855 17.7782 7.28144L13.0597 12L17.7782 16.7186C18.0711 17.0115 18.0711 17.4863 17.7782 17.7792C17.4854 18.0721 17.0105 18.0721 16.7176 17.7792L11.999 13.0607L7.28033 17.7794C6.98744 18.0722 6.51256 18.0722 6.21967 17.7794C5.92678 17.4865 5.92678 17.0116 6.21967 16.7187L10.9384 12L6.21967 7.28131Z"
              fill=""
            />
          </svg>
          <svg
            v-else
            width="16"
            height="12"
            viewBox="0 0 16 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M0.583252 1C0.583252 0.585788 0.919038 0.25 1.33325 0.25H14.6666C15.0808 0.25 15.4166 0.585786 15.4166 1C15.4166 1.41421 15.0808 1.75 14.6666 1.75L1.33325 1.75C0.919038 1.75 0.583252 1.41422 0.583252 1ZM0.583252 11C0.583252 10.5858 0.919038 10.25 1.33325 10.25L14.6666 10.25C15.0808 10.25 15.4166 10.5858 15.4166 11C15.4166 11.4142 15.0808 11.75 14.6666 11.75L1.33325 11.75C0.919038 11.75 0.583252 11.4142 0.583252 11ZM1.33325 5.25C0.919038 5.25 0.583252 5.58579 0.583252 6C0.583252 6.41421 0.919038 6.75 1.33325 6.75L7.99992 6.75C8.41413 6.75 8.74992 6.41421 8.74992 6C8.74992 5.58579 8.41413 5.25 7.99992 5.25L1.33325 5.25Z"
              fill="currentColor"
            />
          </svg>
        </button>

        <HeaderLogo/>

        <!-- Company Badge Display - Desktop -->
        <div v-if="hasCompany" class="hidden lg:block ml-4">
          <CompanyBadge :company="userCompany" />
        </div>

        <!-- Mobile Company Badge (simplified) -->
        <div v-if="hasCompany" class="lg:hidden ml-auto mr-2">
          <span
            class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium"
            :class="{
              'bg-amber-100 text-amber-800 border border-amber-200': userCompany === 'maxstationary',
              'bg-teal-100 text-teal-800 border border-teal-200': userCompany === 'ostnasik',
              'bg-indigo-100 text-indigo-800 border border-indigo-200': userCompany === 'shradhha-ent'
            }"
          >
            {{ companyDisplayName }}
          </span>
        </div>

        <button
          @click="toggleApplicationMenu"
          class="rathod flex items-center justify-center w-10 h-10 text-gray-700 rounded-lg z-99999 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800 lg:hidden"
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M5.99902 10.4951C6.82745 10.4951 7.49902 11.1667 7.49902 11.9951V12.0051C7.49902 12.8335 6.82745 13.5051 5.99902 13.5051C5.1706 13.5051 4.49902 12.8335 4.49902 12.0051V11.9951C4.49902 11.1667 5.1706 10.4951 5.99902 10.4951ZM17.999 10.4951C18.8275 10.4951 19.499 11.1667 19.499 11.9951V12.0051C19.499 12.8335 18.8275 13.5051 17.999 13.5051C17.1706 13.5051 16.499 12.8335 16.499 12.0051V11.9951C16.499 11.1667 17.1706 10.4951 17.999 10.4951ZM13.499 11.9951C13.499 11.1667 12.8275 10.4951 11.999 10.4951C11.1706 10.4951 10.499 11.1667 10.499 11.9951V12.0051C10.499 12.8335 11.1706 13.5051 11.999 13.5051C12.8275 13.5051 13.499 12.8335 13.499 12.0051V11.9951Z"
              fill="currentColor"
            />
          </svg>
        </button>
      </div>

      <div
        :class="[isApplicationMenuOpen ? 'flex' : 'hidden']"
        class="items-center justify-between w-full gap-2 2xsm:gap-5 px-5 py-2 lg:py-0 shadow-theme-md lg:flex lg:justify-end lg:px-0 lg:shadow-none"
      >
        <div class="flex items-center gap-1">
          <!-- Show company badge in mobile menu (optional) -->
          <div v-if="hasCompany" class="lg:hidden mr-2">
            <CompanyBadge :company="userCompany" />
          </div>
          <ThemeToggler />
        </div>
        <UserMenu />
      </div>
    </div>
  </header>
</template>
